import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box, Typography, Paper, Grid, TextField, Button, CircularProgress,
  Tabs, Tab, Divider, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import { INTEGRATION_ICONS, ICON_MAP, DEFAULT_INTEG_CARDS } from "../landingpage/integrationIcons";
import { toast } from "react-toastify";
import {
  getPublicLandingSections,
  saveLandingSection,
  uploadLandingImage,
  getLandingImageUrl,
} from "../../services/landingSectionsService";
import useSettings from "../../hooks/useSettings";

/* ─── Helpers visuais (mesmo padrão do LandingSiteEditor) ─── */

const ImageUpload = ({ value, onChange, label }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const imgUrl = getLandingImageUrl(value);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    const inputEl = e.target;
    if (!file) return;
    setUploading(true);
    try {
      const filename = await uploadLandingImage(file);
      onChange(filename);
      toast.success("Imagem enviada!");
    } catch {
      toast.error("Erro ao enviar imagem.");
    } finally {
      setUploading(false);
      try { inputEl.value = ""; } catch (_) {}
    }
  };

  return (
    <Box marginBottom={2}>
      <Typography variant="caption" style={{ color: "#374151", fontWeight: 600, display: "block", marginBottom: 6 }}>
        {label}
      </Typography>
      {imgUrl && (
        <Box marginBottom={1} style={{ position: "relative", display: "inline-block" }}>
          <img src={imgUrl} alt="preview" style={{ maxHeight: 100, maxWidth: "100%", borderRadius: 8, display: "block", border: "1px solid #e5e7eb" }} />
          <IconButton
            size="small"
            style={{ position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.55)", padding: 3 }}
            onClick={() => onChange("")}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </IconButton>
        </Box>
      )}
      <input type="file" ref={fileRef} style={{ display: "none" }} accept="image/png,image/jpeg,image/gif,image/webp,image/avif,image/svg+xml" onChange={handleFile} />
      <Button variant="outlined" size="small" disabled={uploading} onClick={() => fileRef.current.click()} style={{ fontSize: 12 }}>
        {uploading ? "Enviando..." : (value ? "Trocar imagem" : ("+ " + label))}
      </Button>
    </Box>
  );
};

const ColorPick = ({ label, value, onChange, defaultColor }) => {
  const [hex, setHex] = React.useState(value || "");
  React.useEffect(() => { setHex(value || ""); }, [value]);
  const isValid = v => /^#[0-9A-Fa-f]{6}$/.test(v);
  const handleText = v => {
    setHex(v);
    if (isValid(v) || v === "") onChange(v);
  };
  return (
    <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 16 }}>
      <input
        type="color"
        value={isValid(hex) ? hex : (defaultColor || "#2563EB")}
        onChange={e => { setHex(e.target.value); onChange(e.target.value); }}
        style={{ width: 36, height: 36, border: "1px solid #e5e7eb", borderRadius: 8, cursor: "pointer", padding: 2, flexShrink: 0 }}
      />
      <Box flex={1}>
        <Typography variant="caption" style={{ color: "#374151", fontWeight: 600, display: "block", marginBottom: 2 }}>{label}</Typography>
        <input
          type="text"
          value={hex}
          placeholder={defaultColor || "#2563EB"}
          onChange={e => handleText(e.target.value)}
          style={{ width: "100%", padding: "4px 8px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 13, fontFamily: "monospace", outline: "none", background: "#fff", color: "#374151", boxSizing: "border-box" }}
        />
      </Box>
      {value && (
        <Button size="small" style={{ fontSize: 11, padding: "2px 8px", minWidth: 0, flexShrink: 0 }} onClick={() => { setHex(""); onChange(""); }}>
          Padrao
        </Button>
      )}
    </Box>
  );
};

/* ─── SECTIONS config (idêntico ao LandingSiteEditor) ─── */

const SECTIONS = [
  {
    key: "contact", label: "Contato — WhatsApp",
    defaults: { whatsapp: "552433540335" },
    fields: [
      { id: "whatsapp", label: "Numero do WhatsApp do botao flutuante (com DDI, so numeros. Ex: 5511999998888)", sm: 12 },
    ],
    images: [],
    colors: [],
    sliders: [],
  },
  {
    key: "hero", label: "Hero",
    defaults: { title: "Atendimento no WhatsApp", titleHi: "inteligente e escalavel", sub: "Centralize canais, automatize com IA e gerencie toda sua equipe.", btnPrimary: "Criar conta gratuita →", btnPrimaryLink: "/cadastro", stat1Val: "+500", stat1Label: "Empresas ativas", stat2Val: "+2M", stat2Label: "Mensagens/mes", stat3Val: "99,9%", stat3Label: "Disponibilidade", stat4Val: "4,9★", stat4Label: "Satisfacao" },
    fields: [
      { id: "eyebrow", label: "Badge acima do titulo (vazio para ocultar)", sm: 12 },
      { id: "title", label: "Titulo (linha 1)", sm: 6 },
      { id: "titleHi", label: "Titulo em destaque (linha 2) — vazio para ocultar", sm: 6 },
      { id: "sub", label: "Subtitulo", multiline: true, rows: 2, sm: 12 },
      { id: "btnPrimary", label: "Texto do botao principal", sm: 6 },
      { id: "btnPrimaryLink", label: "Link do botao principal", sm: 6 },
      { id: "btn2Text", label: "Texto do botao secundario", sm: 6 },
      { id: "btn2Link", label: "Link do botao secundario", sm: 6 },
      { id: "stat1Val", label: "Stat 1 — Numero", sm: 3 }, { id: "stat1Label", label: "Stat 1 — Descricao", sm: 3 },
      { id: "stat2Val", label: "Stat 2 — Numero", sm: 3 }, { id: "stat2Label", label: "Stat 2 — Descricao", sm: 3 },
      { id: "stat3Val", label: "Stat 3 — Numero", sm: 3 }, { id: "stat3Label", label: "Stat 3 — Descricao", sm: 3 },
      { id: "stat4Val", label: "Stat 4 — Numero", sm: 3 }, { id: "stat4Label", label: "Stat 4 — Descricao", sm: 3 },
    ],
    images: [{ id: "image", label: "Imagem de fundo do hero" }],
    colors: [
      { id: "bgColor", label: "Cor de fundo do hero", default: "#07090f" },
      { id: "textColor", label: "Cor do titulo", default: "#ffffff" },
      { id: "titleHiColor", label: "Cor da palavra em destaque (vazio = gradiente padrao)", default: "" },
      { id: "eyebrowColor", label: "Badge — cor do texto", default: "#93C5FD" },
      { id: "eyebrowBgColor", label: "Badge — cor de fundo", default: "" },
      { id: "statValueColor", label: "Cor dos numeros das estatisticas", default: "#60A5FA" },
      { id: "statLabelColor", label: "Cor dos textos das estatisticas", default: "#94a3b8" },
      { id: "btnBgFrom", label: "Botao principal — fundo cor inicial (vazio = padrao)", default: "" },
      { id: "btnBgTo", label: "Botao principal — fundo cor final (vazio = padrao)", default: "" },
      { id: "btnTextColor", label: "Botao principal — cor do texto", default: "#ffffff" },
      { id: "btn2TextColor", label: "Botao secundario — cor do texto", default: "#ffffff" },
      { id: "btn2BgColor", label: "Botao secundario — cor de fundo (vazio = padrao)", default: "" },
    ],
    sliders: [{ id: "bgOpacity", label: "Opacidade da imagem de fundo", default: 0.3, min: 0, max: 1, step: 0.05 }],
  },
  {
    key: "features_marketing", label: "Marketing",
    defaults: { tag: "Marketing & Campanhas", title: "Alcance clientes no", titleHi: "momento certo", desc: "Dispare campanhas personalizadas via WhatsApp com textos gerados por IA.", b1: "Campanhas segmentadas por tag, perfil ou historico", b2: "IA cria texto unico para cada contato", b3: "Relatorio de entrega, leitura e resposta em tempo real", btnText: "Ver funcionalidade", btnLink: "/funcionalidades/marketing" },
    fields: [
      { id: "tag", label: "Tag", sm: 6 }, { id: "title", label: "Titulo (linha 1)", sm: 6 },
      { id: "titleHi", label: "Titulo em destaque (linha 2) — vazio para ocultar", sm: 6 },
      { id: "desc", label: "Descricao", multiline: true, rows: 2, sm: 12 },
      { id: "b1", label: "Item de lista 1", sm: 12 }, { id: "b2", label: "Item de lista 2", sm: 12 }, { id: "b3", label: "Item de lista 3", sm: 12 },
      { id: "btnText", label: "Texto do botao", sm: 6 }, { id: "btnLink", label: "Link do botao", sm: 6 },
    ],
    images: [{ id: "image", label: "Imagem da secao" }],
    colors: [
      { id: "accentColor", label: "Cor de destaque", default: "#2563EB" },
      { id: "textColor", label: "Cor do texto", default: "#1a1525" },
      { id: "titleHiColor", label: "Cor da palavra em destaque", default: "#2563EB" },
      { id: "btnColor", label: "Cor do texto do botao", default: "#2563EB" },
      { id: "btnBgColor", label: "Cor de fundo do botao (vazio = transparente)", default: "" },
      { id: "btnHoverBgColor", label: "Botao — hover fundo (vazio = automatico)", default: "" },
      { id: "btnHoverBorderColor", label: "Botao — hover borda (vazio = automatico)", default: "" },
      { id: "btnHoverTextColor", label: "Botao — hover texto (vazio = automatico)", default: "" },
    ],
    sliders: [],
  },
  {
    key: "features_vendas", label: "Vendas",
    defaults: { tag: "Vendas & CRM", title: "Feche mais negocios", titleHi: "sem perder leads", desc: "Gerencie seu pipeline em um Kanban visual com drag-and-drop.", b1: "Kanban drag-and-drop com etapas personalizadas", b2: "Qualificacao automatica via chatbot integrado", b3: "Historico completo de mensagens, notas e arquivos", btnText: "Ver funcionalidade", btnLink: "/funcionalidades/vendas" },
    fields: [
      { id: "tag", label: "Tag", sm: 6 }, { id: "title", label: "Titulo (linha 1)", sm: 6 },
      { id: "titleHi", label: "Titulo em destaque (linha 2) — vazio para ocultar", sm: 6 },
      { id: "desc", label: "Descricao", multiline: true, rows: 2, sm: 12 },
      { id: "b1", label: "Item de lista 1", sm: 12 }, { id: "b2", label: "Item de lista 2", sm: 12 }, { id: "b3", label: "Item de lista 3", sm: 12 },
      { id: "btnText", label: "Texto do botao", sm: 6 }, { id: "btnLink", label: "Link do botao", sm: 6 },
    ],
    images: [{ id: "image", label: "Imagem da secao" }],
    colors: [
      { id: "accentColor", label: "Cor de destaque", default: "#14B8A6" },
      { id: "textColor", label: "Cor do texto", default: "#1a1525" },
      { id: "titleHiColor", label: "Cor da palavra em destaque", default: "#14B8A6" },
      { id: "btnColor", label: "Cor do texto do botao", default: "#14B8A6" },
      { id: "btnBgColor", label: "Cor de fundo do botao (vazio = transparente)", default: "" },
      { id: "btnHoverBgColor", label: "Botao — hover fundo (vazio = automatico)", default: "" },
      { id: "btnHoverBorderColor", label: "Botao — hover borda (vazio = automatico)", default: "" },
      { id: "btnHoverTextColor", label: "Botao — hover texto (vazio = automatico)", default: "" },
    ],
    sliders: [],
  },
  {
    key: "features_suporte", label: "Suporte",
    defaults: { tag: "Suporte & Atendimento", title: "Atenda mais rapido", titleHi: "com menos esforco", desc: "Multiagente em tempo real, filas inteligentes, chatbot 24h.", b1: "Varios agentes simultaneos em um unico numero", b2: "Chatbot builder visual sem programar", b3: "SLA, filas e transferencia com contexto completo", btnText: "Ver funcionalidade", btnLink: "/funcionalidades/suporte" },
    fields: [
      { id: "tag", label: "Tag", sm: 6 }, { id: "title", label: "Titulo (linha 1)", sm: 6 },
      { id: "titleHi", label: "Titulo em destaque (linha 2) — vazio para ocultar", sm: 6 },
      { id: "desc", label: "Descricao", multiline: true, rows: 2, sm: 12 },
      { id: "b1", label: "Item de lista 1", sm: 12 }, { id: "b2", label: "Item de lista 2", sm: 12 }, { id: "b3", label: "Item de lista 3", sm: 12 },
      { id: "btnText", label: "Texto do botao", sm: 6 }, { id: "btnLink", label: "Link do botao", sm: 6 },
    ],
    images: [{ id: "image", label: "Imagem da secao" }],
    colors: [
      { id: "accentColor", label: "Cor de destaque", default: "#2563eb" },
      { id: "textColor", label: "Cor do texto", default: "#1a1525" },
      { id: "titleHiColor", label: "Cor da palavra em destaque", default: "#2563eb" },
      { id: "btnColor", label: "Cor do texto do botao", default: "#2563eb" },
      { id: "btnBgColor", label: "Cor de fundo do botao (vazio = transparente)", default: "" },
      { id: "btnHoverBgColor", label: "Botao — hover fundo (vazio = automatico)", default: "" },
      { id: "btnHoverBorderColor", label: "Botao — hover borda (vazio = automatico)", default: "" },
      { id: "btnHoverTextColor", label: "Botao — hover texto (vazio = automatico)", default: "" },
    ],
    sliders: [],
  },
  {
    key: "how_it_works", label: "Como Funciona",
    defaults: { tag: "Como funciona", title: "Crie experiencias que convertem", sub: "Do primeiro contato ao fechamento.", s1t: "Captacao", s1d: "Aborde visitantes com saudacoes automaticas via chatbot.", s2t: "Qualificacao", s2d: "Conduza o lead pelo funil com perguntas e ofertas personalizadas.", s3t: "Atendimento", s3d: "Transfira leads qualificados para seus agentes em tempo real.", s4t: "Conversao", s4d: "Deixe clientes comprarem, agendarem ou tirarem duvidas com IA 24h." },
    fields: [
      { id: "tag", label: "Texto do badge da secao", sm: 12 },
      { id: "title", label: "Titulo", sm: 6 }, { id: "sub", label: "Subtitulo", multiline: true, rows: 2, sm: 12 },
      { id: "s1t", label: "Passo 1 — Titulo", sm: 4 }, { id: "s1d", label: "Passo 1 — Descricao", multiline: true, rows: 2, sm: 8 },
      { id: "s2t", label: "Passo 2 — Titulo", sm: 4 }, { id: "s2d", label: "Passo 2 — Descricao", multiline: true, rows: 2, sm: 8 },
      { id: "s3t", label: "Passo 3 — Titulo", sm: 4 }, { id: "s3d", label: "Passo 3 — Descricao", multiline: true, rows: 2, sm: 8 },
      { id: "s4t", label: "Passo 4 — Titulo", sm: 4 }, { id: "s4d", label: "Passo 4 — Descricao", multiline: true, rows: 2, sm: 8 },
    ],
    images: [{ id: "image", label: "Imagem do visual" }],
    colors: [
      { id: "bgColor", label: "Fundo da secao (vazio = padrao)", default: "" },
      { id: "textColor", label: "Cor do titulo principal", default: "#1a1525" },
      { id: "tagBgColor", label: "Badge — fundo (vazio = roxo claro padrao)", default: "" },
      { id: "tagColor", label: "Badge — cor do texto (vazio = roxo padrao)", default: "" },
      { id: "stepNumBgColor", label: "Numero do passo — fundo", default: "#EFF6FF" },
      { id: "stepNumColor", label: "Numero do passo — cor do texto", default: "#2563EB" },
      { id: "stepConnectorColor", label: "Linha conectora entre passos", default: "#e8eaed" },
      { id: "stepTitleColor", label: "Titulo de cada passo", default: "#0f0f23" },
      { id: "stepDescColor", label: "Descricao de cada passo", default: "#6b7280" },
    ],
    sliders: [],
  },
  {
    key: "cta", label: "CTA",
    defaults: { eyebrow: "Comece hoje", title: "Transforme seu atendimento", sub: "Centralize canais, automatize com IA e gerencie toda sua equipe.", btn: "Criar conta gratuita →", btnLink: "/cadastro", trust1: "Sem cartao de credito", trust2: "Suporte em portugues", trust3: "Cancele quando quiser" },
    fields: [
      { id: "eyebrow", label: "Texto acima do titulo", sm: 6 }, { id: "title", label: "Titulo", sm: 6 },
      { id: "sub", label: "Subtitulo", multiline: true, rows: 2, sm: 12 },
      { id: "btn", label: "Texto do botao", sm: 6 }, { id: "btnLink", label: "Link do botao", sm: 6 },
      { id: "trust1", label: "Item de confianca 1 (vazio = padrao)", sm: 4 },
      { id: "trust2", label: "Item de confianca 2 (vazio = padrao)", sm: 4 },
      { id: "trust3", label: "Item de confianca 3 (vazio = padrao)", sm: 4 },
    ],
    images: [],
    colors: [
      { id: "gradFrom", label: "Gradiente — cor inicial", default: "#2563EB" },
      { id: "gradTo", label: "Gradiente — cor final", default: "#2563eb" },
      { id: "textColor", label: "Cor do texto", default: "#ffffff" },
      { id: "btnTextColor", label: "Cor do texto do botao", default: "#2563EB" },
      { id: "btnBgColor", label: "Cor de fundo do botao", default: "#ffffff" },
      { id: "btnHoverBgColor", label: "Botao CTA — hover fundo (vazio = automatico)", default: "" },
      { id: "btnHoverTextColor", label: "Botao CTA — hover texto (vazio = automatico)", default: "" },
    ],
    sliders: [],
  },
  {
    key: "header", label: "Header / Nav",
    defaults: {
      nav1Label: "Funcionalidades", nav1Link: "#features",
      nav2Label: "Integracoes", nav2Link: "#integrations",
      nav3Label: "Planos", nav3Link: "#pricing",
      nav4Label: "FAQ", nav4Link: "#faq",
      nav5Label: "Blog", nav5Link: "#blog",
      btnLoginLabel: "Entrar", btnLoginLink: "/login",
      btnCadastroLabel: "Comecar gratis", btnCadastroLink: "/cadastro",
    },
    fields: [
      { id: "nav1Label", label: "Menu 1 — Texto", sm: 6 }, { id: "nav1Link", label: "Menu 1 — Link", sm: 6 },
      { id: "nav2Label", label: "Menu 2 — Texto", sm: 6 }, { id: "nav2Link", label: "Menu 2 — Link", sm: 6 },
      { id: "nav3Label", label: "Menu 3 — Texto", sm: 6 }, { id: "nav3Link", label: "Menu 3 — Link", sm: 6 },
      { id: "nav4Label", label: "Menu 4 — Texto", sm: 6 }, { id: "nav4Link", label: "Menu 4 — Link", sm: 6 },
      { id: "nav5Label", label: "Menu 5 — Texto", sm: 6 }, { id: "nav5Link", label: "Menu 5 — Link", sm: 6 },
      { id: "btnLoginLabel", label: "Botao Login — Texto", sm: 6 }, { id: "btnLoginLink", label: "Botao Login — Link", sm: 6 },
      { id: "btnCadastroLabel", label: "Botao CTA — Texto", sm: 6 }, { id: "btnCadastroLink", label: "Botao CTA — Link", sm: 6 },
    ],
    images: [],
    colors: [
      { id: "navTextColor", label: "Cor dos links de navegacao", default: "#64748b" },
      { id: "btnLoginTextColor", label: "Botao Entrar — cor do texto", default: "#64748b" },
      { id: "btnLoginBgColor", label: "Botao Entrar — cor de fundo (vazio = transparente)", default: "" },
      { id: "btnLoginBorderColor", label: "Botao Entrar — cor da borda", default: "#e5e7eb" },
      { id: "btnCadastroTextColor", label: "Botao CTA — cor do texto", default: "#ffffff" },
      { id: "btnCadastroBgFrom", label: "Botao CTA — fundo cor inicial (vazio = padrao)", default: "" },
      { id: "btnCadastroBgTo", label: "Botao CTA — fundo cor final (vazio = padrao)", default: "" },
      { id: "hoverAccentColor", label: "Hover — cor de destaque dos links do menu", default: "" },
      { id: "btnLoginHoverTextColor", label: "Botao Entrar — hover texto", default: "" },
      { id: "btnLoginHoverBgColor", label: "Botao Entrar — hover fundo", default: "" },
      { id: "btnLoginHoverBorderColor", label: "Botao Entrar — hover borda", default: "" },
      { id: "btnCadastroHoverTextColor", label: "Botao CTA — hover texto", default: "" },
      { id: "btnCadastroBgHoverFrom", label: "Botao CTA — hover fundo inicio", default: "" },
      { id: "btnCadastroBgHoverTo", label: "Botao CTA — hover fundo fim", default: "" },
    ],
    sliders: [],
  },
  {
    key: "marquee", label: "Faixa de Recursos (ticker)",
    defaults: {
      item1label: "WhatsApp Business",   item1color: "#14B8A6",
      item2label: "Instagram Direct",    item2color: "#E4405F",
      item3label: "Facebook Messenger",  item3color: "#1877F2",
      item4label: "Agente de IA GPT-4",  item4color: "#60A5FA",
      item5label: "Kanban de Leads",     item5color: "#f59e0b",
      item6label: "Campanhas em Massa",  item6color: "#ec4899",
      item7label: "API & Webhooks",      item7color: "#38bdf8",
      item8label: "Relatorios em Tempo Real", item8color: "#34d399",
      item9label: "Multi Atendimento",   item9color: "#f97316",
      item10label: "Chatbot Builder",    item10color: "#2563EB",
      item11label: "Agendamentos",       item11color: "#0EA5E9",
      item12label: "N8N & Typebot",      item12color: "#2563EB",
    },
    fields: [
      { id: "item1label",  label: "Item 1 — Texto",  sm: 6 }, { id: "item2label",  label: "Item 2 — Texto",  sm: 6 },
      { id: "item3label",  label: "Item 3 — Texto",  sm: 6 }, { id: "item4label",  label: "Item 4 — Texto",  sm: 6 },
      { id: "item5label",  label: "Item 5 — Texto",  sm: 6 }, { id: "item6label",  label: "Item 6 — Texto",  sm: 6 },
      { id: "item7label",  label: "Item 7 — Texto",  sm: 6 }, { id: "item8label",  label: "Item 8 — Texto",  sm: 6 },
      { id: "item9label",  label: "Item 9 — Texto",  sm: 6 }, { id: "item10label", label: "Item 10 — Texto", sm: 6 },
      { id: "item11label", label: "Item 11 — Texto", sm: 6 }, { id: "item12label", label: "Item 12 — Texto", sm: 6 },
    ],
    images: [],
    colors: [
      { id: "bgColor",   label: "Cor de fundo da faixa",      default: "#080a18" },
      { id: "textColor", label: "Cor do texto dos itens",     default: "#94a3b8" },
      { id: "item1color",  label: "Dot item 1",  default: "#14B8A6" },
      { id: "item2color",  label: "Dot item 2",  default: "#E4405F" },
      { id: "item3color",  label: "Dot item 3",  default: "#1877F2" },
      { id: "item4color",  label: "Dot item 4",  default: "#60A5FA" },
      { id: "item5color",  label: "Dot item 5",  default: "#f59e0b" },
      { id: "item6color",  label: "Dot item 6",  default: "#ec4899" },
      { id: "item7color",  label: "Dot item 7",  default: "#38bdf8" },
      { id: "item8color",  label: "Dot item 8",  default: "#34d399" },
      { id: "item9color",  label: "Dot item 9",  default: "#f97316" },
      { id: "item10color", label: "Dot item 10", default: "#2563EB" },
      { id: "item11color", label: "Dot item 11", default: "#0EA5E9" },
      { id: "item12color", label: "Dot item 12", default: "#2563EB" },
    ],
    sliders: [],
  },
  {
    key: "footer", label: "Footer",
    defaults: {
      tagline: "Plataforma omnichannel com IA para transformar seu atendimento e multiplicar resultados.",
      facebook: "", instagram: "", whatsapp: "", youtube: "", linkedin: "", tiktok: "", cnpj: "",
      col1title: "", col1l1: "", col1h1: "", col1l2: "", col1h2: "", col1l3: "", col1h3: "", col1l4: "", col1h4: "", col1l5: "", col1h5: "",
      col2title: "", col2l1: "", col2h1: "", col2l2: "", col2h2: "", col2l3: "", col2h3: "", col2l4: "", col2h4: "", col2l5: "", col2h5: "",
      col3title: "", col3l1: "", col3h1: "", col3l2: "", col3h2: "", col3l3: "", col3h3: "", col3l4: "", col3h4: "", col3l5: "", col3h5: "",
    },
    fields: [
      { id: "tagline", label: "Texto abaixo da logo (tagline)", multiline: true, rows: 2, sm: 12 },
      { id: "cnpj", label: "CNPJ (exibido no rodape)", sm: 6 },
      { id: "facebook", label: "Facebook URL", sm: 6 },
      { id: "instagram", label: "Instagram URL", sm: 6 },
      { id: "whatsapp", label: "WhatsApp Link (https://wa.me/...)", sm: 6 },
      { id: "youtube", label: "YouTube URL", sm: 6 },
      { id: "linkedin", label: "LinkedIn URL", sm: 6 },
      { id: "tiktok", label: "TikTok URL", sm: 6 },
      /* Coluna 1 */
      { id: "col1title", label: "── Coluna 1 — Titulo", sm: 12 },
      { id: "col1l1", label: "Coluna 1 — Link 1 — Texto", sm: 6 }, { id: "col1h1", label: "Coluna 1 — Link 1 — URL", sm: 6 },
      { id: "col1l2", label: "Coluna 1 — Link 2 — Texto", sm: 6 }, { id: "col1h2", label: "Coluna 1 — Link 2 — URL", sm: 6 },
      { id: "col1l3", label: "Coluna 1 — Link 3 — Texto", sm: 6 }, { id: "col1h3", label: "Coluna 1 — Link 3 — URL", sm: 6 },
      { id: "col1l4", label: "Coluna 1 — Link 4 — Texto", sm: 6 }, { id: "col1h4", label: "Coluna 1 — Link 4 — URL", sm: 6 },
      { id: "col1l5", label: "Coluna 1 — Link 5 — Texto", sm: 6 }, { id: "col1h5", label: "Coluna 1 — Link 5 — URL", sm: 6 },
      /* Coluna 2 */
      { id: "col2title", label: "── Coluna 2 — Titulo", sm: 12 },
      { id: "col2l1", label: "Coluna 2 — Link 1 — Texto", sm: 6 }, { id: "col2h1", label: "Coluna 2 — Link 1 — URL", sm: 6 },
      { id: "col2l2", label: "Coluna 2 — Link 2 — Texto", sm: 6 }, { id: "col2h2", label: "Coluna 2 — Link 2 — URL", sm: 6 },
      { id: "col2l3", label: "Coluna 2 — Link 3 — Texto", sm: 6 }, { id: "col2h3", label: "Coluna 2 — Link 3 — URL", sm: 6 },
      { id: "col2l4", label: "Coluna 2 — Link 4 — Texto", sm: 6 }, { id: "col2h4", label: "Coluna 2 — Link 4 — URL", sm: 6 },
      { id: "col2l5", label: "Coluna 2 — Link 5 — Texto", sm: 6 }, { id: "col2h5", label: "Coluna 2 — Link 5 — URL", sm: 6 },
      /* Coluna 3 */
      { id: "col3title", label: "── Coluna 3 — Titulo", sm: 12 },
      { id: "col3l1", label: "Coluna 3 — Link 1 — Texto", sm: 6 }, { id: "col3h1", label: "Coluna 3 — Link 1 — URL", sm: 6 },
      { id: "col3l2", label: "Coluna 3 — Link 2 — Texto", sm: 6 }, { id: "col3h2", label: "Coluna 3 — Link 2 — URL", sm: 6 },
      { id: "col3l3", label: "Coluna 3 — Link 3 — Texto", sm: 6 }, { id: "col3h3", label: "Coluna 3 — Link 3 — URL", sm: 6 },
      { id: "col3l4", label: "Coluna 3 — Link 4 — Texto", sm: 6 }, { id: "col3h4", label: "Coluna 3 — Link 4 — URL", sm: 6 },
      { id: "col3l5", label: "Coluna 3 — Link 5 — Texto", sm: 6 }, { id: "col3h5", label: "Coluna 3 — Link 5 — URL", sm: 6 },
    ],
    images: [
      { id: "logo", label: "Logo do footer (PNG/SVG recomendado, fundo transparente)" },
    ],
    colors: [
      { id: "bgColor", label: "Fundo do footer (vazio = padrao escuro)", default: "" },
      { id: "socialIconColor", label: "Icones sociais — cor do icone", default: "#60A5FA" },
      { id: "socialIconBgColor", label: "Icones sociais — cor de fundo (vazio = padrao)", default: "" },
      { id: "socialIconHoverBg", label: "Icones sociais — hover fundo (vazio = usa cor do icone)", default: "" },
      { id: "socialIconHoverColor", label: "Icones sociais — hover cor do icone", default: "#ffffff" },
      { id: "brandColor", label: "Cor do nome da marca", default: "#60A5FA" },
      { id: "textColor", label: "Cor da tagline / texto", default: "" },
    ],
    sliders: [],
  },
  {
    key: "integrations", label: "Integracoes",
    defaults: { eyebrow: "INTEGRACOES", sectionTitle: "Conectado a todos os canais", sectionSub: "Integre com as ferramentas que voce ja usa." },
    fields: [
      { id: "eyebrow", label: "Badge da secao (vazio para ocultar)", sm: 12 },
      { id: "sectionTitle", label: "Titulo da secao", sm: 6 },
      { id: "sectionSub", label: "Subtitulo", multiline: true, rows: 2, sm: 12 },
    ],
    images: [],
    colors: [
      { id: "bgColor", label: "Fundo da secao", default: "#fafafa" },
      { id: "tagBgColor", label: "Badge — fundo (vazio = padrao)", default: "" },
      { id: "tagColor", label: "Badge — cor do texto (vazio = padrao)", default: "" },
      { id: "cardBgColor", label: "Fundo dos cards", default: "#ffffff" },
      { id: "cardBorderColor", label: "Borda dos cards", default: "#e8eaed" },
      { id: "titleColor", label: "Cor do titulo dos cards", default: "#0f0f23" },
      { id: "cardDescColor", label: "Cor da descricao dos cards", default: "#64748b" },
      { id: "linkColor", label: "Cor dos links 'Ver detalhes'", default: "#2563EB" },
      { id: "accentColor", label: "Cor de destaque (hover, borda ativa)", default: "#93C5FD" },
    ],
    sliders: [],
  },
  {
    key: "pricing", label: "Planos / Precos",
    defaults: { eyebrow: "PLANOS", sectionTitle: "Preco justo para cada fase", sectionSub: "Comece com o que precisa e evolua conforme seu negocio cresce.", badgeText: "Mais popular", featBtnText: "Comecar agora →", normalBtnText: "Assinar plano" },
    fields: [
      { id: "eyebrow", label: "Badge da secao (vazio para ocultar)", sm: 12 },
      { id: "sectionTitle", label: "Titulo da secao", sm: 6 },
      { id: "sectionSub", label: "Subtitulo", multiline: true, rows: 2, sm: 12 },
      { id: "badgeText", label: "Texto do badge 'Mais popular'", sm: 6 },
      { id: "featBtnText", label: "Botao card destaque — texto", sm: 6 },
      { id: "normalBtnText", label: "Botao cards normais — texto", sm: 6 },
    ],
    images: [],
    colors: [
      { id: "bgColor", label: "Fundo da secao", default: "" },
      { id: "tagBgColor", label: "Badge — fundo (vazio = padrao)", default: "" },
      { id: "tagColor", label: "Badge — cor do texto (vazio = padrao)", default: "" },
      { id: "cardBgColor", label: "Fundo dos cards normais", default: "#ffffff" },
      { id: "cardBorderColor", label: "Borda dos cards normais", default: "#e8eaed" },
      { id: "featBgFrom", label: "Card destaque — fundo inicial", default: "#2563EB" },
      { id: "featBgTo", label: "Card destaque — fundo final", default: "#2563eb" },
      { id: "textColor", label: "Cor do texto dos cards", default: "#0f0f23" },
      { id: "accentColor", label: "Cor de destaque (checkmarks, botao outline)", default: "#2563EB" },
      { id: "badgeBgColor", label: "Badge 'Mais popular' — fundo", default: "#0d0f1c" },
      { id: "badgeTextColor", label: "Badge 'Mais popular' — cor do texto", default: "#ffffff" },
      { id: "disabledIconColor", label: "Icone de item desativado (X) — cor", default: "#d1d5db" },
    ],
    sliders: [],
  },
  {
    key: "blog", label: "Blog",
    defaults: { sectionTag: "Blog", sectionTitle: "Ultimos artigos", sectionSub: "Dicas, novidades e tendencias sobre atendimento e CRM." },
    fields: [
      { id: "sectionTag", label: "Badge da secao (vazio para ocultar)", sm: 12 },
      { id: "sectionTitle", label: "Titulo da secao", sm: 6 },
      { id: "sectionSub", label: "Subtitulo", multiline: true, rows: 2, sm: 12 },
    ],
    images: [],
    colors: [
      { id: "bgColor", label: "Fundo da secao", default: "#ffffff" },
      { id: "tagBgColor", label: "Badge — fundo (vazio = padrao)", default: "" },
      { id: "tagColor", label: "Badge — cor do texto (vazio = padrao)", default: "" },
      { id: "cardBgColor", label: "Fundo dos cards de artigo", default: "#ffffff" },
      { id: "cardBorderColor", label: "Borda dos cards de artigo", default: "#e8eaed" },
      { id: "titleColor", label: "Cor do titulo dos artigos", default: "#0d0f1c" },
      { id: "dateColor", label: "Cor da data dos artigos", default: "#94a3b8" },
      { id: "resumoColor", label: "Cor do resumo dos artigos", default: "#64748b" },
      { id: "arrowHoverBg", label: "Seta carrossel — hover fundo (vazio = automatico)", default: "" },
      { id: "arrowHoverColor", label: "Seta carrossel — hover icone (vazio = automatico)", default: "" },
    ],
    sliders: [],
  },
  {
    key: "testimonials_meta", label: "Depoimentos — Textos e Cores",
    defaults: { eyebrow: "DEPOIMENTOS", sectionTitle: "O que nossos clientes dizem", sectionSub: "Historias reais de empresas que transformaram seu atendimento." },
    fields: [
      { id: "eyebrow", label: "Badge da secao (vazio para ocultar)", sm: 12 },
      { id: "sectionTitle", label: "Titulo da secao", sm: 6 },
      { id: "sectionSub", label: "Subtitulo", multiline: true, rows: 2, sm: 12 },
    ],
    images: [],
    colors: [
      { id: "bgColor", label: "Fundo da secao", default: "#f8f7ff" },
      { id: "tagBgColor", label: "Badge — fundo (vazio = padrao)", default: "" },
      { id: "tagColor", label: "Badge — cor do texto (vazio = padrao)", default: "" },
      { id: "cardBgColor", label: "Fundo do card", default: "#ffffff" },
      { id: "cardBorderColor", label: "Borda do card", default: "#E6F7FF" },
      { id: "quoteColor", label: "Cor do texto do depoimento", default: "#1f2937" },
      { id: "starColor", label: "Cor das estrelas", default: "#f59e0b" },
      { id: "accentColor", label: "Cor de destaque (dot ativo, hover)", default: "#2563EB" },
    ],
    sliders: [],
  },
  {
    key: "faq_meta", label: "FAQ — Textos e Cores",
    defaults: { eyebrow: "DUVIDAS", sectionTitle: "Perguntas frequentes", sectionSub: "Tudo que voce precisa saber antes de comecar." },
    fields: [
      { id: "eyebrow", label: "Badge da secao (vazio para ocultar)", sm: 12 },
      { id: "sectionTitle", label: "Titulo da secao", sm: 6 },
      { id: "sectionSub", label: "Subtitulo", multiline: true, rows: 2, sm: 12 },
    ],
    images: [],
    colors: [
      { id: "bgColor", label: "Fundo da secao", default: "" },
      { id: "tagBgColor", label: "Badge — fundo (vazio = padrao)", default: "" },
      { id: "tagColor", label: "Badge — cor do texto (vazio = padrao)", default: "" },
      { id: "cardBgColor", label: "Fundo dos cards", default: "#ffffff" },
      { id: "cardBorderColor", label: "Borda dos cards", default: "#e8eaed" },
      { id: "titleColor", label: "Cor das perguntas", default: "#0f0f23" },
      { id: "bodyColor", label: "Cor das respostas", default: "#6b7280" },
      { id: "accentColor", label: "Cor de destaque (aberto/hover)", default: "#2563EB" },
    ],
    sliders: [],
  },
  {
    key: "cookies", label: "Banner de Cookies",
    defaults: {
      message: "Usamos cookies para melhorar sua experiencia. Ao continuar navegando, voce concorda com nossa",
      btnLabel: "Aceitar",
      linkLabel: "Politica de Privacidade",
      linkHref: "/politica-de-privacidade",
    },
    fields: [
      { id: "message", label: "Texto do banner", multiline: true, rows: 3, sm: 12 },
      { id: "btnLabel", label: "Texto do botao Aceitar", sm: 6 },
      { id: "linkLabel", label: "Texto do link (vazio = sem link)", sm: 6 },
      { id: "linkHref", label: "Link (URL ou /rota)", sm: 6 },
    ],
    images: [],
    colors: [
      { id: "bgColor", label: "Fundo do banner", default: "#1a1a2e" },
      { id: "textColor", label: "Cor do texto", default: "#e5e7eb" },
      { id: "btnBgColor", label: "Botao — fundo", default: "#2563EB" },
      { id: "btnTextColor", label: "Botao — cor do texto", default: "#ffffff" },
      { id: "linkColor", label: "Cor do link", default: "#60A5FA" },
    ],
    sliders: [],
  },
];

/* ─── Painel de Depoimentos ─── */

const DEFAULT_TESTIMONIALS = [
  { name: "Mariana Costa", role: "Gestora de Operacoes", quote: "Transformamos completamente nosso atendimento!", color: "#2563EB", photo: "" },
  { name: "Ricardo Alves", role: "Diretor Comercial", quote: "Centralizamos tudo. Nosso tempo de resposta caiu 70%.", color: "#2563eb", photo: "" },
  { name: "Fernanda Lima", role: "Proprietaria", quote: "O chatbot atende 24h fora do horario comercial.", color: "#14B8A6", photo: "" },
];

const TestimonialsPanel = ({ initialData, onSave, onChange }) => {
  const [items, setItems] = useState(() => {
    if (initialData?.items?.length) return initialData.items;
    return [...DEFAULT_TESTIMONIALS];
  });
  const [saving, setSaving] = useState(false);
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });

  useEffect(() => {
    if (initialData?.items?.length) setItems(initialData.items);
  }, [initialData]); // eslint-disable-line

  const setT = useCallback((idx, field, val) => {
    setItems(prev => {
      const next = prev.map((t, i) => i === idx ? { ...t, [field]: val } : t);
      if (onChangeRef.current) onChangeRef.current({ items: next });
      return next;
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave("testimonials", { items });
      toast.success("Depoimentos salvos!");
    } catch {
      toast.error("Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
        <Typography variant="body2" style={{ color: "#6b7280" }}>
          Adicione depoimentos com foto e cor personalizada.
        </Typography>
        <Button variant="outlined" size="small" startIcon={<AddIcon />}
          onClick={() => setItems(prev => {
            const next = [...prev, { name: "", role: "", quote: "", color: "#2563EB", photo: "" }];
            if (onChangeRef.current) onChangeRef.current({ items: next });
            return next;
          })}>
          + Depoimento
        </Button>
      </Box>

      {items.map((t, idx) => {
        const initials = (t.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
        const photoUrl = getLandingImageUrl(t.photo);
        return (
          <Paper key={idx} variant="outlined" style={{ padding: 16, marginBottom: 16, borderRadius: 10, background: "#fafafa" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
              <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: t.color || "#2563EB", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 13, overflow: "hidden", flexShrink: 0 }}>
                  {photoUrl ? <img src={photoUrl} alt="" style={{ width: 36, height: 36, objectFit: "cover" }} /> : initials}
                </div>
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>Depoimento {idx + 1}</Typography>
              </Box>
              <IconButton size="small" onClick={() => setItems(prev => {
                const next = prev.filter((_, i) => i !== idx);
                if (onChangeRef.current) onChangeRef.current({ items: next });
                return next;
              })}>
                <DeleteIcon fontSize="small" style={{ color: "#ef4444" }} />
              </IconButton>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="Nome" variant="outlined" fullWidth size="small" value={t.name || ""} onChange={e => setT(idx, "name", e.target.value)} InputProps={{ style: { backgroundColor: "#fff" } }} style={{ marginBottom: 10 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Cargo / Empresa" variant="outlined" fullWidth size="small" value={t.role || ""} onChange={e => setT(idx, "role", e.target.value)} InputProps={{ style: { backgroundColor: "#fff" } }} style={{ marginBottom: 10 }} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Depoimento" variant="outlined" fullWidth size="small" multiline minRows={3} value={t.quote || ""} onChange={e => setT(idx, "quote", e.target.value)} InputProps={{ style: { backgroundColor: "#fff" } }} style={{ marginBottom: 10 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ImageUpload value={t.photo} onChange={val => setT(idx, "photo", val)} label="Foto do depoente" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ColorPick label="Cor do avatar" value={t.color} onChange={val => setT(idx, "color", val)} defaultColor="#2563EB" />
              </Grid>
            </Grid>
          </Paper>
        );
      })}

      <Box display="flex" justifyContent="flex-end" marginTop={2}>
        <Button variant="contained" color="primary" size="small" disabled={saving} onClick={handleSave}>
          {saving ? <CircularProgress size={18} /> : "Salvar depoimentos"}
        </Button>
      </Box>
    </Box>
  );
};

/* ─── Painel de FAQ ─── */

const DEFAULT_FAQS = [
  { q: "Quantos atendentes posso ter?", a: "Depende do plano escolhido." },
  { q: "A IA responde automaticamente?", a: "Sim. Configure o prompt e a IA assume o atendimento." },
  { q: "Quais canais sao suportados?", a: "WhatsApp, Facebook Messenger, Instagram Direct e e-mail." },
];

const FaqPanel = ({ initialData, onSave, onChange }) => {
  const [items, setItems] = useState(() => {
    if (initialData?.items?.length) return initialData.items;
    return [...DEFAULT_FAQS];
  });
  const [saving, setSaving] = useState(false);
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });

  useEffect(() => {
    if (initialData?.items?.length) setItems(initialData.items);
  }, [initialData]); // eslint-disable-line

  const setF = useCallback((idx, field, val) => {
    setItems(prev => {
      const next = prev.map((f, i) => i === idx ? { ...f, [field]: val } : f);
      if (onChangeRef.current) onChangeRef.current({ items: next });
      return next;
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave("faq", { items });
      toast.success("FAQ salvo!");
    } catch {
      toast.error("Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {items.map((faq, idx) => (
        <Box key={idx} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginBottom: 12, position: "relative" }}>
          <IconButton size="small" style={{ position: "absolute", top: 8, right: 8, color: "#ef4444" }} onClick={() => setItems(prev => {
            const next = prev.filter((_, i) => i !== idx);
            if (onChangeRef.current) onChangeRef.current({ items: next });
            return next;
          })}>
            <DeleteIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" style={{ fontWeight: 700, color: "#374151", marginBottom: 6, display: "block" }}>
            Pergunta {idx + 1}
          </Typography>
          <TextField label="Pergunta" variant="outlined" fullWidth size="small" value={faq.q || ""} onChange={e => setF(idx, "q", e.target.value)} style={{ marginBottom: 10 }} InputProps={{ style: { backgroundColor: "#fff" } }} />
          <TextField label="Resposta" variant="outlined" fullWidth size="small" multiline minRows={2} value={faq.a || ""} onChange={e => setF(idx, "a", e.target.value)} InputProps={{ style: { backgroundColor: "#fff" } }} />
        </Box>
      ))}
      <Box display="flex" justifyContent="space-between" alignItems="center" marginTop={2}>
        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setItems(prev => {
          const next = [...prev, { q: "", a: "" }];
          if (onChangeRef.current) onChangeRef.current({ items: next });
          return next;
        })}>
          + Pergunta
        </Button>
        <Button variant="contained" color="primary" size="small" disabled={saving} onClick={handleSave}>
          {saving ? <CircularProgress size={18} /> : "Salvar FAQ"}
        </Button>
      </Box>
    </Box>
  );
};

/* ─── Painel Marquee (faixa rotativa) ─── */

const MARQUEE_DEFAULTS = [
  { label: "WhatsApp Business",       color: "#14B8A6" },
  { label: "Instagram Direct",         color: "#E4405F" },
  { label: "Facebook Messenger",       color: "#1877F2" },
  { label: "Agente de IA GPT-4",       color: "#60A5FA" },
  { label: "Kanban de Leads",          color: "#f59e0b" },
  { label: "Campanhas em Massa",       color: "#ec4899" },
  { label: "API & Webhooks",           color: "#38bdf8" },
  { label: "Relatórios em Tempo Real", color: "#34d399" },
  { label: "Multi Atendimento",        color: "#f97316" },
  { label: "Chatbot Builder",          color: "#2563EB" },
  { label: "Agendamentos",             color: "#0EA5E9" },
  { label: "N8N & Typebot",            color: "#2563EB" },
];

const MarqueePanel = ({ initialData, onSave, onChange }) => {
  const init = () => {
    const d = initialData || {};
    const base = {
      bgColor:   d.bgColor   || "#080a18",
      textColor: d.textColor || "#94a3b8",
    };
    MARQUEE_DEFAULTS.forEach((def, idx) => {
      const n = idx + 1;
      base[`item${n}label`] = d[`item${n}label`] != null ? d[`item${n}label`] : def.label;
      base[`item${n}color`] = d[`item${n}color`] || def.color;
      base[`item${n}icon`]  = d[`item${n}icon`]  || "";
    });
    return base;
  };

  const [local, setLocal] = useState(init);
  const [saving, setSaving] = useState(false);
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });

  const set = useCallback((field, val) => {
    setLocal(prev => {
      const next = { ...prev, [field]: val };
      if (onChangeRef.current) onChangeRef.current(next);
      return next;
    });
  }, []);

  return (
    <Box>
      <Typography variant="body2" style={{ color: "#6b7280", marginBottom: 16 }}>
        Configure os itens da faixa animada. Deixe o texto em branco para ocultar o item.
        O campo de ícone aceita emoji — se preenchido, substitui o ponto colorido.
      </Typography>

      {/* Configurações globais */}
      <Box display="flex" style={{ gap: 24, marginBottom: 24 }}>
        <ColorPick label="Cor de fundo da faixa" value={local.bgColor} defaultColor="#080a18" onChange={v => set("bgColor", v)} />
        <ColorPick label="Cor do texto dos itens" value={local.textColor} defaultColor="#94a3b8" onChange={v => set("textColor", v)} />
      </Box>

      <Divider style={{ marginBottom: 16 }} />
      <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 12, color: "#374151" }}>
        Itens da faixa
      </Typography>

      {MARQUEE_DEFAULTS.map((_, idx) => {
        const n = idx + 1;
        return (
          <Box key={n} display="flex" alignItems="center" style={{ gap: 8, marginBottom: 10 }}>
            <TextField
              label="Emoji"
              value={local[`item${n}icon`]}
              onChange={e => set(`item${n}icon`, e.target.value)}
              size="small"
              variant="outlined"
              style={{ width: 90 }}
              inputProps={{ maxLength: 4, style: { fontSize: 20, textAlign: "center" } }}
              placeholder="🚀"
              title="Emoji ou símbolo. Se preenchido, substitui o ponto colorido."
            />
            <TextField
              label={`Item ${n} — Texto`}
              value={local[`item${n}label`]}
              onChange={e => set(`item${n}label`, e.target.value)}
              size="small"
              variant="outlined"
              style={{ flex: 1 }}
            />
            <Box style={{ flexShrink: 0 }}>
              <ColorPick
                label="Cor do ponto"
                value={local[`item${n}color`]}
                defaultColor={MARQUEE_DEFAULTS[idx].color}
                onChange={v => set(`item${n}color`, v)}
              />
            </Box>
          </Box>
        );
      })}

      <Box marginTop={2}>
        <Button
          variant="contained"
          color="primary"
          disabled={saving}
          onClick={async () => {
            setSaving(true);
            try {
              await onSave("marquee", local);
              toast.success("Faixa salva!");
            } catch {
              toast.error("Erro ao salvar.");
            } finally {
              setSaving(false);
            }
          }}
        >
          {saving ? <CircularProgress size={18} /> : "Salvar Faixa"}
        </Button>
      </Box>
    </Box>
  );
};

/* ─── Painel de seção genérica ─── */

const SectionPanel = ({ section, initialData, onSave, onChange }) => {
  const [local, setLocal] = useState(() => ({ ...section.defaults, ...(initialData || {}) }));
  const [saving, setSaving] = useState(false);
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });

  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setLocal({ ...section.defaults, ...initialData });
    }
  }, [initialData]); // eslint-disable-line

  const set = useCallback((field, val) => {
    setLocal(prev => {
      const next = { ...prev, [field]: val };
      if (onChangeRef.current) onChangeRef.current(next);
      return next;
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(section.key, local);
      toast.success("Seção salva!");
    } catch {
      toast.error("Erro ao salvar seção.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" style={{ color: "#6b7280", marginBottom: 16 }}>
        Campos em branco usam o texto padrão do sistema.
      </Typography>

      <Grid container spacing={2}>
        {section.fields.map(f => (
          <Grid key={f.id} item xs={12} sm={f.sm || 6}>
            <TextField
              label={f.label}
              variant="outlined"
              fullWidth
              size="small"
              multiline={f.multiline}
              minRows={f.multiline ? (f.rows || 3) : undefined}
              value={local[f.id] != null ? local[f.id] : ""}
              onChange={e => set(f.id, e.target.value)}
              placeholder={section.defaults[f.id] || ""}
              InputProps={{ style: { backgroundColor: "#fff" } }}
              style={{ marginBottom: 12 }}
            />
          </Grid>
        ))}
      </Grid>

      {section.images.length > 0 && (
        <>
          <Divider style={{ margin: "16px 0" }} />
          <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 10, color: "#374151" }}>Imagens</Typography>
          <Grid container spacing={2}>
            {section.images.map(img => (
              <Grid key={img.id} item xs={12} sm={6}>
                <ImageUpload value={local[img.id] || ""} onChange={val => set(img.id, val)} label={img.label} />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {section.colors.length > 0 && (
        <>
          <Divider style={{ margin: "16px 0" }} />
          <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 10, color: "#374151" }}>Cores</Typography>
          <Grid container spacing={2}>
            {section.colors.map(col => (
              <Grid key={col.id} item xs={12} sm={6}>
                <ColorPick label={col.label} value={local[col.id] || ""} onChange={val => set(col.id, val)} defaultColor={col.default} />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {section.sliders && section.sliders.length > 0 && (
        <>
          <Divider style={{ margin: "16px 0" }} />
          <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 10, color: "#374151" }}>Opacidade</Typography>
          {section.sliders.map(sl => {
            const val = local[sl.id] !== undefined ? Number(local[sl.id]) : (sl.default || 0.3);
            return (
              <Box key={sl.id} marginBottom={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={0.5}>
                  <Typography variant="caption" style={{ color: "#374151", fontWeight: 600 }}>{sl.label}</Typography>
                  <Typography variant="caption" style={{ color: "#6b7280", fontFamily: "monospace" }}>{Math.round(val * 100)}%</Typography>
                </Box>
                <input type="range" min={sl.min !== undefined ? sl.min : 0} max={sl.max !== undefined ? sl.max : 1} step={sl.step || 0.05} value={val} onChange={e => set(sl.id, parseFloat(e.target.value))} style={{ width: "100%", cursor: "pointer", accentColor: "#2563EB" }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="caption" style={{ color: "#9ca3af", fontSize: 10 }}>Transparente</Typography>
                  <Typography variant="caption" style={{ color: "#9ca3af", fontSize: 10 }}>Opaco</Typography>
                </Box>
              </Box>
            );
          })}
        </>
      )}

      <Box display="flex" justifyContent="flex-end" marginTop={3}>
        <Button variant="contained" color="primary" disabled={saving} onClick={handleSave}>
          {saving ? <CircularProgress size={18} /> : "Salvar seção"}
        </Button>
      </Box>
    </Box>
  );
};

/* ─── Painel de cards de Integrações ─── */

const EMPTY_CARD = (icons) => {
  const ic = icons[0];
  return { id: String(Date.now()), iconKey: ic.key, iconBg: ic.defaultBg, iconColor: ic.defaultColor, iconType: "preset", iconImage: "", title: "", desc: "", cardBg: "", cardBorder: "", titleColor: "", descColor: "" };
};

const IntegrationCardsPanel = ({ initialData, onSave }) => {
  const [cards, setCards] = useState(
    (initialData && initialData.cards && initialData.cards.length >= 3)
      ? initialData.cards
      : DEFAULT_INTEG_CARDS
  );
  const [saving, setSaving] = useState(false);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const isNew = editing && !cards.some(c => c.id === editing.id);

  const openEdit = (card) => { setEditing({ ...card }); setDlgOpen(true); };
  const openAdd = () => { setEditing(EMPTY_CARD(INTEGRATION_ICONS)); setDlgOpen(true); };
  const closeDlg = () => { setDlgOpen(false); setEditing(null); };

  const handleDelete = (id) => {
    if (cards.length <= 3) return;
    setCards(prev => prev.filter(c => c.id !== id));
  };

  const handleDlgSave = () => {
    if (!editing || !editing.title.trim() || !editing.desc.trim()) {
      return;
    }
    setCards(prev => {
      const idx = prev.findIndex(c => c.id === editing.id);
      if (idx >= 0) { const n = [...prev]; n[idx] = editing; return n; }
      return [...prev, editing];
    });
    closeDlg();
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await onSave("integrations_cards", { cards });
      toast.success("Cards salvos!");
    } catch (_) {
      toast.error("Erro ao salvar cards.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" style={{ color: "#6b7280", marginBottom: 16 }}>
        Mínimo 3, máximo 9 cards. Clique no ícone de editar para modificar ou no lixo para remover.
      </Typography>

      {cards.map((card) => {
        const ic = ICON_MAP[card.iconKey] || INTEGRATION_ICONS[0];
        const cardIconUrl = card.iconType === "upload" && card.iconImage ? getLandingImageUrl(card.iconImage) : null;
        return (
          <Box key={card.id} display="flex" alignItems="center" style={{ gap: 12, marginBottom: 10, padding: "10px 14px", background: "#f9fafb", borderRadius: 10, border: "1px solid #e5e7eb" }}>
            <Box style={{ width: 44, height: 44, borderRadius: 10, background: card.iconBg || "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
              {cardIconUrl
                ? <img src={cardIconUrl} alt={card.title} style={{ width: 30, height: 30, objectFit: "contain" }} />
                : ic.render(card.iconColor)
              }
            </Box>
            <Box flex={1} style={{ minWidth: 0 }}>
              <Typography style={{ fontWeight: 700, fontSize: 14, color: "#1f2937" }}>{card.title || "(sem título)"}</Typography>
              <Typography variant="caption" style={{ color: "#6b7280", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{card.desc || "(sem descrição)"}</Typography>
            </Box>
            <Box display="flex" style={{ gap: 4, flexShrink: 0 }}>
              <IconButton size="small" onClick={() => openEdit(card)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              </IconButton>
              <IconButton size="small" disabled={cards.length <= 3} onClick={() => handleDelete(card.id)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cards.length <= 3 ? "#d1d5db" : "#ef4444"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
              </IconButton>
            </Box>
          </Box>
        );
      })}

      <Box display="flex" justifyContent="space-between" alignItems="center" marginTop={2}>
        <Button variant="outlined" size="small" startIcon={<AddIcon />} disabled={cards.length >= 9} onClick={openAdd}>
          {cards.length >= 9 ? "Máximo atingido (9)" : `Novo Card (${cards.length}/9)`}
        </Button>
        <Button variant="contained" color="primary" disabled={saving} onClick={handleSaveAll}>
          {saving ? <CircularProgress size={18} /> : "Salvar Cards"}
        </Button>
      </Box>

      <Dialog open={dlgOpen} onClose={closeDlg} maxWidth="sm" fullWidth>
        <DialogTitle style={{ fontWeight: 700, fontSize: 17 }}>{isNew ? "Novo Card" : "Editar Card"}</DialogTitle>
        <DialogContent style={{ maxHeight: "80vh", overflowY: "auto" }}>
          {editing && (
            <Box paddingTop={1}>

              {/* ── Tipo de ícone ── */}
              <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 10, color: "#374151" }}>Ícone</Typography>
              <Box display="flex" style={{ gap: 8, marginBottom: 16 }}>
                <Button
                  variant={editing.iconType !== "upload" ? "contained" : "outlined"}
                  size="small"
                  color={editing.iconType !== "upload" ? "primary" : "default"}
                  onClick={() => setEditing(prev => ({ ...prev, iconType: "preset" }))}
                  style={{ fontSize: 12 }}
                >
                  Ícone predefinido
                </Button>
                <Button
                  variant={editing.iconType === "upload" ? "contained" : "outlined"}
                  size="small"
                  color={editing.iconType === "upload" ? "primary" : "default"}
                  onClick={() => setEditing(prev => ({ ...prev, iconType: "upload" }))}
                  style={{ fontSize: 12 }}
                >
                  Upload PNG
                </Button>
              </Box>

              {editing.iconType === "upload" ? (
                <Box style={{ marginBottom: 18 }}>
                  <ImageUpload
                    value={editing.iconImage || ""}
                    onChange={val => setEditing(prev => ({ ...prev, iconImage: val }))}
                    label="Ícone PNG (recomendado 64×64px, fundo transparente)"
                  />
                  {editing.iconImage && getLandingImageUrl(editing.iconImage) && (
                    <Box style={{ marginTop: 8, padding: 12, background: editing.iconBg || "#f3f4f6", borderRadius: 12, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                      <img src={getLandingImageUrl(editing.iconImage)} alt="preview" style={{ width: 40, height: 40, objectFit: "contain" }} />
                    </Box>
                  )}
                </Box>
              ) : (
                <Box style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 6, marginBottom: 18 }}>
                  {INTEGRATION_ICONS.map(ic => {
                    const selected = editing.iconKey === ic.key;
                    return (
                      <Box
                        key={ic.key}
                        title={ic.label}
                        onClick={() => setEditing(prev => ({ ...prev, iconKey: ic.key, iconBg: ic.defaultBg, iconColor: ic.defaultColor }))}
                        style={{ width: 46, height: 46, borderRadius: 10, background: selected ? ic.defaultBg : "#f3f4f6", border: `2px solid ${selected ? ic.defaultColor : "transparent"}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all .15s" }}
                      >
                        {ic.render(selected ? ic.defaultColor : "#9ca3af")}
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* ── Fundo e cor do ícone ── */}
              <Grid container spacing={2} style={{ marginBottom: 4 }}>
                <Grid item xs={6}>
                  <ColorPick label="Fundo do ícone" value={editing.iconBg || ""} onChange={v => setEditing(prev => ({ ...prev, iconBg: v }))} defaultColor="#f3f4f6" />
                </Grid>
                {editing.iconType !== "upload" && (
                  <Grid item xs={6}>
                    <ColorPick label="Cor do ícone (SVG)" value={editing.iconColor || ""} onChange={v => setEditing(prev => ({ ...prev, iconColor: v }))} defaultColor="#2563EB" />
                  </Grid>
                )}
              </Grid>

              <Divider style={{ margin: "12px 0 16px" }} />

              {/* ── Textos ── */}
              <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 10, color: "#374151" }}>Conteúdo</Typography>
              <TextField label="Título" variant="outlined" fullWidth size="small" value={editing.title} onChange={e => { e.persist(); setEditing(prev => ({ ...prev, title: e.target.value })); }} style={{ marginBottom: 12 }} InputProps={{ style: { background: "#fff" } }} />
              <TextField label="Descrição" variant="outlined" fullWidth size="small" multiline rows={3} value={editing.desc} onChange={e => { e.persist(); setEditing(prev => ({ ...prev, desc: e.target.value })); }} InputProps={{ style: { background: "#fff" } }} />

              <Divider style={{ margin: "16px 0" }} />

              {/* ── Cores do card ── */}
              <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 10, color: "#374151" }}>Cores do card (substituem o padrão da seção)</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <ColorPick label="Fundo do card (vazio = padrão)" value={editing.cardBg || ""} onChange={v => setEditing(prev => ({ ...prev, cardBg: v }))} defaultColor="#ffffff" />
                </Grid>
                <Grid item xs={6}>
                  <ColorPick label="Borda do card (vazio = padrão)" value={editing.cardBorder || ""} onChange={v => setEditing(prev => ({ ...prev, cardBorder: v }))} defaultColor="#e8edf3" />
                </Grid>
                <Grid item xs={6}>
                  <ColorPick label="Cor do título do card" value={editing.titleColor || ""} onChange={v => setEditing(prev => ({ ...prev, titleColor: v }))} defaultColor="#0d0f1c" />
                </Grid>
                <Grid item xs={6}>
                  <ColorPick label="Cor da descrição do card" value={editing.descColor || ""} onChange={v => setEditing(prev => ({ ...prev, descColor: v }))} defaultColor="#64748b" />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <Button onClick={closeDlg}>Cancelar</Button>
          <Button variant="contained" color="primary" disabled={!editing || !editing.title.trim() || !editing.desc.trim()} onClick={handleDlgSave}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/* ─── Painel de texto (Termos / Política) ─── */

const TextEditorPanel = ({ settingKey, title, hint }) => {
  const { getAll, update } = useSettings();
  const [text, setText] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getAll()
      .then(data => {
        const val = data.find(s => s.key === settingKey)?.value || "";
        setText(val);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [settingKey]); // eslint-disable-line

  const handleSave = async () => {
    setSaving(true);
    try {
      await update({ key: settingKey, value: text });
      toast.success("Texto salvo com sucesso!");
    } catch {
      toast.error("Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <Box display="flex" justifyContent="center" padding={4}><CircularProgress size={24} /></Box>;

  return (
    <Box>
      <Typography variant="body2" style={{ color: "#6b7280", marginBottom: 16 }}>{hint}</Typography>
      <TextField
        label={title}
        multiline
        rows={22}
        fullWidth
        variant="outlined"
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="Digite aqui. Suporta HTML. Ex: <h2>Título</h2><p>...</p>"
        InputProps={{ style: { backgroundColor: "#ffffff", fontFamily: "monospace", fontSize: 13 } }}
      />
      <Typography variant="caption" style={{ color: "#999", marginTop: 4, display: "block" }}>
        Suporta HTML. Exemplo: {"<h2>Título</h2><p>Texto do parágrafo.</p>"}
      </Typography>
      <Box display="flex" justifyContent="flex-end" marginTop={2}>
        <Button variant="contained" color="primary" disabled={saving} onClick={handleSave}>
          {saving ? <CircularProgress size={18} /> : "Salvar texto"}
        </Button>
      </Box>
    </Box>
  );
};

/* ─── Página principal ─── */

const TABS = [
  ...SECTIONS.map(s => ({ key: s.key, label: s.label, type: s.key === "marquee" ? "marquee" : "section" })),
  { key: "integrations_cards", label: "Integrações — Cards", type: "integrations-cards" },
  { key: "testimonials", label: "Depoimentos", type: "testimonials" },
  { key: "faq", label: "FAQ", type: "faq" },
  { key: "terms_text", label: "Termos de Serviço", type: "text-editor", settingKey: "termsOfServiceText", title: "Texto dos Termos de Serviço", hint: "Este texto será exibido na página /termos-de-servico. Suporta HTML." },
  { key: "usage_text", label: "Política de Uso", type: "text-editor", settingKey: "usagePolicyText", title: "Texto da Política de Uso", hint: "Este texto será exibido na página /politica-de-uso. Suporta HTML." },
  { key: "privacy_text", label: "Privacidade", type: "text-editor", settingKey: "privacyText", title: "Texto da Política de Privacidade", hint: "Este texto será exibido na página /politica-de-privacidade. Suporta HTML." },
];

const SECTION_ANCHORS = {
  header: "hero", hero: "hero", marquee: "marquee",
  features_marketing: "features", features_vendas: "features", features_suporte: "features",
  how_it_works: "how",
  integrations: "integrations", integrations_cards: "integrations",
  pricing: "pricing",
  testimonials_meta: "testimonials", testimonials: "testimonials",
  faq_meta: "faq", faq: "faq",
  cta: "cta", blog: "blog", footer: "footer",
};

export default function LandingPageAdmin() {
  const [activeTab, setActiveTab] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [remote, setRemote] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const iframeRef = useRef(null);
  const liveDataRef = useRef({});
  const previewWrapRef = useRef(null);
  const [desktopScale, setDesktopScale] = useState(0.45);
  const DESKTOP_W = 1440;
  const DESKTOP_H = 900; // viewport desktop padrão (determina 100vh dentro do iframe)

  useEffect(() => {
    getPublicLandingSections()
      .then(data => {
        const d = data || {};
        setRemote(d);
        liveDataRef.current = { ...d };
      })
      .catch(() => setRemote({}))
      .finally(() => setLoaded(true));
  }, []);

  const handleSave = useCallback(async (sectionKey, content) => {
    await saveLandingSection(sectionKey, content);
    setRemote(prev => ({ ...prev, [sectionKey]: content }));
    liveDataRef.current = { ...liveDataRef.current, [sectionKey]: content };
  }, []);

  const sendPreview = useCallback((sectionKey, sectionData) => {
    liveDataRef.current = { ...liveDataRef.current, [sectionKey]: sectionData };
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        { type: "LP_PREVIEW_UPDATE", data: liveDataRef.current },
        "*"
      );
    }
  }, []);

  const handleIframeLoad = useCallback(() => {
    // Aguarda o React da landing page montar e registrar o listener antes de enviar
    const send = () => {
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          { type: "LP_PREVIEW_UPDATE", data: liveDataRef.current },
          "*"
        );
      }
    };
    setTimeout(send, 400);
    setTimeout(send, 1200); // segunda tentativa caso a primeira chegue antes do listener
  }, []);

  useEffect(() => {
    if (!previewOpen || !iframeRef.current?.contentWindow) return;
    const tab = TABS[activeTab];
    if (!tab) return;
    const anchor = SECTION_ANCHORS[tab.key] || "hero";
    // Pequeno delay para garantir que o iframe ja processou o ultimo update
    const t = setTimeout(() => {
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage({ type: "LP_SCROLL_TO", anchor }, "*");
      }
    }, 150);
    return () => clearTimeout(t);
  }, [activeTab, previewOpen]);

  // Calcula o zoom para desktop: renderiza em 1440px e escala para caber no painel
  useEffect(() => {
    if (!previewOpen) return;
    const update = () => {
      if (previewWrapRef.current) {
        const w = previewWrapRef.current.offsetWidth;
        if (w > 0) setDesktopScale(w / DESKTOP_W);
      }
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [previewOpen]);

  if (!loaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  const currentTab = TABS[activeTab];

  const formContent = (
    <>
      <Box marginBottom={2} display="flex" alignItems="flex-start" justifyContent="space-between" style={{ gap: 12 }}>
        <Box>
          <Typography variant="h5" style={{ fontWeight: 700, color: "#1a1a2e", marginBottom: 4 }}>
            Editor de Landing Page
          </Typography>
          <Typography variant="body2" style={{ color: "#6b7280" }}>
            Selecione a aba e edite a seção. Clique fora dos campos para confirmar, depois em "Salvar seção".
          </Typography>
        </Box>
        <Button
          variant={previewOpen ? "contained" : "outlined"}
          color="primary"
          size="small"
          onClick={() => setPreviewOpen(v => !v)}
          style={{ flexShrink: 0, marginTop: 4, whiteSpace: "nowrap" }}
        >
          {previewOpen ? "Fechar preview" : "Pré-visualizar"}
        </Button>
      </Box>

      <Paper elevation={0} style={{ border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          style={{ borderBottom: "1px solid #e5e7eb", background: "#f9fafb" }}
          indicatorColor="primary"
          textColor="primary"
        >
          {TABS.map((tab, i) => (
            <Tab key={tab.key} label={tab.label} style={{ fontWeight: activeTab === i ? 700 : 400, minWidth: 100, fontSize: 13 }} />
          ))}
        </Tabs>

        <Box padding={3}>
          {currentTab.type === "section" && (
            <SectionPanel
              key={currentTab.key}
              section={SECTIONS.find(s => s.key === currentTab.key)}
              initialData={remote[currentTab.key]}
              onSave={handleSave}
              onChange={data => sendPreview(currentTab.key, data)}
            />
          )}
          {currentTab.type === "integrations-cards" && (
            <IntegrationCardsPanel
              key="integrations_cards"
              initialData={remote.integrations_cards}
              onSave={handleSave}
            />
          )}
          {currentTab.type === "marquee" && (
            <MarqueePanel
              key="marquee"
              initialData={remote.marquee}
              onSave={handleSave}
              onChange={data => sendPreview("marquee", data)}
            />
          )}
          {currentTab.type === "testimonials" && (
            <TestimonialsPanel
              key="testimonials"
              initialData={remote.testimonials}
              onSave={handleSave}
              onChange={data => sendPreview("testimonials", data)}
            />
          )}
          {currentTab.type === "faq" && (
            <FaqPanel
              key="faq"
              initialData={remote.faq}
              onSave={handleSave}
              onChange={data => sendPreview("faq", data)}
            />
          )}
          {currentTab.type === "text-editor" && (
            <TextEditorPanel
              key={currentTab.key}
              settingKey={currentTab.settingKey}
              title={currentTab.title}
              hint={currentTab.hint}
            />
          )}
        </Box>
      </Paper>
    </>
  );

  if (!previewOpen) {
    return <Box>{formContent}</Box>;
  }

  return (
    <Box style={{ display: "grid", gridTemplateColumns: "44% 1fr", gap: 16, alignItems: "flex-start" }}>
      {/* Painel do formulário */}
      <Box style={{ minWidth: 0 }}>
        {formContent}
      </Box>

      {/* Painel de preview */}
      <Box style={{
        position: "sticky",
        top: 16,
        height: "88vh",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        background: "#fff",
        boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
      }}>
        {/* Barra do preview */}
        <Box style={{
          padding: "6px 14px",
          borderBottom: "1px solid #e5e7eb",
          background: "#f9fafb",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#ef4444", display: "inline-block" }} />
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#f59e0b", display: "inline-block" }} />
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#10b981", display: "inline-block" }} />
            <Typography variant="caption" style={{ color: "#6b7280", marginLeft: 4, fontSize: 11 }}>
              {currentTab.label}
            </Typography>
          </Box>
          {/* Toggle desktop/mobile */}
          <Box style={{ display: "flex", gap: 4 }}>
            {[
              { key: "desktop", title: "Desktop", icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg> },
              { key: "mobile",  title: "Mobile",  icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><circle cx="12" cy="18" r="1" fill="currentColor"/></svg> },
            ].map(d => (
              <button
                key={d.key}
                title={d.title}
                onClick={() => setPreviewDevice(d.key)}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  width: 28, height: 28, borderRadius: 6, border: "none", cursor: "pointer",
                  background: previewDevice === d.key ? "#2563EB" : "transparent",
                  color: previewDevice === d.key ? "#fff" : "#6b7280",
                  transition: "background .15s, color .15s",
                }}
              >
                {d.icon}
              </button>
            ))}
          </Box>
        </Box>

        {/* Área do iframe */}
        <Box
          ref={previewWrapRef}
          style={{
            flex: 1,
            overflow: "hidden",
            position: "relative",
            background: previewDevice === "mobile" ? "#1a1a2e" : "#f3f4f6",
          }}
        >
          <iframe
            ref={iframeRef}
            src="/"
            style={previewDevice === "desktop" ? {
              // transform: scale preserva o viewport interno (100vh = DESKTOP_H = 900px),
              // diferente de zoom que expandia 100vh para o valor declarado (3000px → hero gigante)
              border: "none",
              position: "absolute",
              top: 0,
              left: 0,
              width: `${DESKTOP_W}px`,
              height: `${DESKTOP_H}px`,
              transform: `scale(${desktopScale})`,
              transformOrigin: "top left",
              pointerEvents: "none", // bloqueia rolagem manual dentro do iframe
            } : {
              border: "none",
              position: "absolute",
              top: "20px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "390px",
              height: "calc(100% - 40px)",
              borderRadius: 12,
              boxShadow: "0 8px 48px rgba(0,0,0,0.5)",
            }}
            title="Pré-visualização da Landing Page"
            onLoad={handleIframeLoad}
          />
        </Box>
      </Box>
    </Box>
  );
}
